import { autorun } from "mobx";
import { useEffect, useRef, useState } from "react";
import { SequenceStateStore } from "../store/SequenceStateStore";
import { SequenceTimelineStore } from "../store/SequenceTimelineStore";
import { createHighPPICanvas } from "../../librettoEditor/utils/canvas";
import { drawCanvasTimelineRuler } from "../../librettoEditor/modules/timeline/components/CanvasTimeRuler/utils/canvasRuler";
import { TIMELINE_RULER_TICK_WIDTH_PX } from "../../librettoEditor/config/config";
import { useTheme } from "@mui/material/styles";
import './SequenceCanvasTimeRuler.styles.scss';

const resolveSequenceRulerFillColor = (isDarkMode: boolean) => {
  if (typeof window !== "undefined") {
    const cssValue = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue("--app-text-subtle")
      .trim();
    if (cssValue) return cssValue;
  }
  return isDarkMode ? "#A0A0A0" : "#707070";
};

const SequenceCanvasTimeRuler = () => {
  const theme = useTheme();
  const [duration, setDuration] = useState(SequenceStateStore.duration);
  const [timelineZoom, setTimelineZoom] = useState(SequenceTimelineStore.zoom);
  const [scrollLeft, setScrollLeft] = useState(0);

  const canvasContainerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasContextRef = useRef<CanvasRenderingContext2D | null>(null);
  const canvasFillColorRef = useRef<string | null>(null);

  useEffect(() => {
    const disposeAutorun = autorun(() => {
      setDuration(SequenceStateStore.duration);
      setTimelineZoom(SequenceTimelineStore.zoom);
    });
    window.addEventListener('resize', resizeCanvas);
    return () => {
      disposeAutorun();
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  useEffect(() => {
    if (canvasRef.current) {
      canvasContextRef.current = canvasRef.current.getContext('2d');
      canvasFillColorRef.current = resolveSequenceRulerFillColor(theme.palette.mode === "dark");
      resizeCanvas();
    }
  }, []);

  useEffect(() => {
    canvasFillColorRef.current = resolveSequenceRulerFillColor(theme.palette.mode === "dark");
    redrawCanvas();
  }, [theme.palette.mode]);

  useEffect(() => {
    const handleTracksScrollLeft = (event: any) => {
      setScrollLeft(event.detail);
    };
    window.addEventListener('sequenceTracksScrollLeft', handleTracksScrollLeft);
    return () => window.removeEventListener('sequenceTracksScrollLeft', handleTracksScrollLeft);
  }, []);

  useEffect(() => {
    redrawCanvas();
  }, [duration, scrollLeft, timelineZoom]);

  const LEFT_PAD_SECONDS = SequenceTimelineStore.leftPadSeconds ?? 3;

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canvasContainerRef.current) return;
    const { left } = canvasContainerRef.current.getBoundingClientRect();
    const x = event.clientX - left + scrollLeft;
    const leftPadPx = LEFT_PAD_SECONDS * TIMELINE_RULER_TICK_WIDTH_PX * timelineZoom;
    const xAdjusted = x - leftPadPx;
    const customEvent = new CustomEvent('sequenceSeekingTimeRulerStart', {
      bubbles: true,
      detail: { event, x: xAdjusted },
    });
    canvasContainerRef.current.dispatchEvent(customEvent as any);
  };

  const resizeCanvas = () => {
    if (!canvasContainerRef.current || !canvasContextRef.current) return;
    const height = canvasContainerRef.current.clientHeight;
    const width = canvasContainerRef.current.clientWidth;
    createHighPPICanvas(canvasContextRef.current, width, height);
    redrawCanvas();
  };

  const redrawCanvas = () => {
    if (canvasContextRef.current) {
      drawCanvasTimelineRuler(canvasContextRef.current, duration, {
        scrollOffset: scrollLeft,
        fillColor: canvasFillColorRef.current ?? '#000',
        zoom: timelineZoom,
        leftPadSeconds: LEFT_PAD_SECONDS,
      });
    }
  };

  return (
    <div className="canvas-time-ruler" onMouseDown={handleMouseDown} ref={canvasContainerRef}>
      <canvas ref={canvasRef as any}></canvas>
    </div>
  );
};

export default SequenceCanvasTimeRuler;
